// Print / PDF helpers shared by the APAR full report screens
// (HrAparFullReport and EmployeeCompletedReport).

export const APAR_PRINT_ROOT_ID = "apar-report-content";

const KEEP_ATTR = "data-apar-print-keep";
const PDF_EXPORT_CLASS = "apar-pdf-export";

/**
 * Tags the report element and every ancestor up to <html> so the print
 * stylesheet can hide everything that is not on that path (header, sidebar,
 * toasts, toolbar) while leaving the report itself in normal document flow.
 *
 * Call it once the report has rendered; the tags make Ctrl+P work exactly like
 * the Print button.
 */
export const markAparPrintRoot = () => {
  const el = document.getElementById(APAR_PRINT_ROOT_ID);
  if (!el) return;
  let node: HTMLElement | null = el;
  while (node) {
    node.setAttribute(KEEP_ATTR, "");
    node = node.parentElement;
  }
};

/** Removes the tags added by markAparPrintRoot — call on unmount. */
export const clearAparPrintRoot = () => {
  const marked = document.querySelectorAll("[" + KEEP_ATTR + "]");
  for (let i = 0; i < marked.length; i++) {
    marked[i].removeAttribute(KEEP_ATTR);
  }
};

export const printAparReport = () => {
  markAparPrintRoot();
  window.print();
};

/**
 * A4 portrait export.
 *
 * The capture runs against an off-screen *clone* of the sheet, never the live
 * one. The clone is forced to 190mm — A4 width less the 10mm side margins
 * passed to html2pdf — so html2canvas captures it at the exact size of the
 * printable area and jsPDF places it 1:1 without rescaling. Applying that width
 * to the live element instead would drop its `margin: 0 auto` and visibly jerk
 * the report leftwards for as long as the export takes.
 *
 * Page breaks are pushed to section boundaries and never fall inside a table
 * row or between a question bar and its answer box.
 */
export const downloadAparReportPdf = (filename: string) => {
  const el = document.getElementById(APAR_PRINT_ROOT_ID);
  if (!el) return;

  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const html2pdf = require("html2pdf.js");

  const source = el.cloneNode(true) as HTMLElement;
  source.removeAttribute("id"); // the live sheet keeps sole ownership of the id
  source.removeAttribute(KEEP_ATTR);
  source.classList.add(PDF_EXPORT_CLASS);

  // Parked off-screen: it still lays out and inherits the stylesheet, but the
  // user never sees it.
  const holder = document.createElement("div");
  holder.setAttribute(
    "style",
    "position:fixed;left:-10000px;top:0;width:210mm;pointer-events:none;"
  );
  holder.appendChild(source);
  document.body.appendChild(holder);

  const restore = () => {
    if (holder.parentNode) holder.parentNode.removeChild(holder);
  };

  return html2pdf()
    .set({
      margin: [12, 10, 12, 10], // top, left, bottom, right (mm)
      filename,
      image: { type: "jpeg", quality: 0.98 },
      // scrollX/Y pinned to 0 so a scrolled page does not shift the capture.
      // windowWidth is deliberately left at the real viewport width, otherwise
      // the clone would evaluate the mobile media queries.
      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollX: 0,
        scrollY: 0,
      },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait", compress: true },
      pagebreak: {
        mode: ["css", "legacy"],
        before: [".apar-section-break"],
        avoid: ["tr", "img", ".apar-q-label", ".apar-avoid-break"],
      },
    })
    .from(source)
    .save()
    .then(restore)
    .catch(restore);
};

/** Builds the download file name, stripping characters a file system rejects. */
export const aparPdfFileName = (empName: string, cycleName: string) =>
  ("APAR_Report_" + (empName || "Employee") + "_" + (cycleName || "Report"))
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, "_") + ".pdf";
